import { useRef, useState, useEffect, useMemo } from "react";

import { useParams, useNavigate } from "react-router-dom";
import Button from "../../../components/ui/Button";
import { FaArrowLeft } from "react-icons/fa";
import ConfirmationModal from "../../../components/ui/ConfirmationModal";
import InvoicePrintContent from "./InvoicePrintContent";
import { useInvoice } from "@/hooks/useInvoices";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import QueryStateWrapper from "@/components/ui/QueryStateWrapper";
import InvoiceItemsScroll from "./InvoiceItemsScroll";
import { getApiInstance } from "@/utils/api";
import { generateNearpayReceiptHTML } from "./NearpayReceipt";
// Removed API fetching for item names; we will rely on invoice response data

const InvoiceDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: invoice, isLoading, error } = useInvoice(Number(id));

  const [showPrintModal, setShowPrintModal] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);

  const [enrichedItems, setEnrichedItems] = useState<any[]>([]);
  const [isPrintingReceipt, setIsPrintingReceipt] = useState(false);

  // Web print fallback: open HTML in a new window and trigger print
  const printHtmlInBrowser = (html: string) => {
    try {
      const printWindow = window.open("", "_blank", "width=400,height=600");
      if (!printWindow) {
        window.print();
        return;
      }
      printWindow.document.open();
      printWindow.document.write(`<!DOCTYPE html>
        <html dir="rtl" lang="ar">
          <head>
            <meta charset="UTF-8" />
            <title>Print</title>
            <style>
              @page { size: 58mm auto; margin: 0; }
              html, body { margin: 0; padding: 0; background: #fff; }
            </style>
          </head>
          <body>${html}</body>
        </html>`);
      printWindow.document.close();
      printWindow.focus();
      printWindow.onload = () => {
        printWindow.print();
        setTimeout(() => printWindow.close(), 100);
      };
    } catch {
      window.print();
    }
  };

  const formDefaultValues = useMemo(
    () => ({
      phone: invoice?.phone && invoice.phone !== "string" ? invoice.phone : "",
    }),
    [invoice?.phone]
  );

  const { reset } = useForm({
    defaultValues: formDefaultValues,
  });

  useEffect(() => {
    reset(formDefaultValues);
  }, [formDefaultValues, reset]);

  useEffect(() => {
    if (id && Number(id)) {
      queryClient.refetchQueries({ queryKey: ["invoices", Number(id)] });
    }
  }, [id, queryClient]);

  useEffect(() => {
    if (!invoice) return;
    const rawItems = invoice.order?.items ?? [];
    const itemsWithDetails = rawItems.map((item: any, index: number) => {
      // أخذ الاسم مباشرة من response
      const nameFromService = item?.service_info?.name;
      const nameFromSubscription = item?.subscription_info?.name;
      const name = nameFromService || nameFromSubscription || "عنصر غير محدد";

      return {
        id: item.id || index,
        name,
        price: (item.price ?? 0).toString(),
        // قيم تفصيلية لكل عنصر من الباك اند لعرضها في الجدول
        tax: item?.tax ?? 0,
        price_before_tax: item?.price_before_tax ?? 0,
        priceBeforeTax: item?.price_before_tax ?? 0,
        priceAfterTax: item?.price ?? 0,
        quantity: 1,
      };
    });
    setEnrichedItems(itemsWithDetails);
  }, [invoice]);

  const handleGoBack = () => {
    navigate(-1);
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className='p-8 text-center text-gray-600'>جاري التحميل...</div>
      );
    }

    if (!invoice) {
      return (
        <div className='p-4 max-w-4xl mx-auto space-y-6 text-center'>
          <div className='bg-red-50 border border-red-200 rounded-lg p-6'>
            <div className='text-red-600 text-xl mb-4'>⚠️</div>
            <h2 className='text-xl font-bold text-red-600 mb-2'>
              لم يتم العثور على الفاتورة!
            </h2>
            <p className='text-gray-600 mb-4'>
              الفاتورة رقم {id} غير موجودة أو تم حذفها
            </p>
            <Button
              type='secondary'
              onClick={handleGoBack}
              className='bg-red-600 text-white hover:bg-red-700'
            >
              العودة للفواتير
            </Button>
          </div>
        </div>
      );
    }

    const order = invoice.order || {};
    const orderItems = order?.items ?? [];
    const totalPrice = orderItems.reduce(
      (sum, item) => sum + Number(item.price),
      0
    );
    const totalBefore = totalPrice;
    // استخلاص قيم المبلغ المستحق من الباك اند
    const orderTotals = (invoice as any)?.order?.total_price as
      | { total_before?: number; total_after?: number }
      | undefined;
    const backendTotalBefore =
      typeof orderTotals?.total_before === "number"
        ? orderTotals?.total_before
        : undefined;
    const backendTotalAfter =
      typeof orderTotals?.total_after === "number"
        ? orderTotals?.total_after
        : undefined;
    const backendSingleTotal =
      typeof (invoice as any)?.total_price === "number"
        ? ((invoice as any)?.total_price as number)
        : undefined;
    const discountApplied =
      (typeof backendTotalBefore === "number" &&
        typeof backendTotalAfter === "number" &&
        backendTotalBefore !== backendTotalAfter) ||
      ((invoice as any)?.discount_percentage || 0) > 0;
    const totalQuantity = order?.items?.length || 0;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    // @ts-ignore: بعض الفواتير تأتي من الباك اند مع user_info داخل order
    const phoneFallback =
      (order as any)?.user_info?.phone ||
      invoice.user_info?.phone ||
      invoice.phone ||
      "غير متوفر";

    const printInvoice = {
      id: invoice.id,
      clientName: "عميل",
      phone: phoneFallback,
      discountCode:
        invoice.coupon_code && invoice.coupon_code !== "string"
          ? invoice.coupon_code
          : "لا يوجد",
      totalPrice: totalPrice,
      cashAmount: invoice.cash,
      cardAmount: invoice.visa,
      points: invoice.points || 0,
      date: new Date(invoice.created_at).toLocaleDateString("ar-EG"),
      time: new Date(invoice.created_at).toLocaleTimeString("ar-EG", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }),
      qrCode: (invoice as any).qr_code,
      // تمرير العناصر الأصلية لتوفير subscription_info في الطباعة
      order: {
        items: order?.items ?? [],
      },
      // Backend totals mapping
      totalBeforeDiscount:
        (invoice as any).total_before_discount ??
        (invoice as any).order?.total_price?.total_before,
      totalBeforeTax: (invoice as any).total_before_tax,
      totalTax: (invoice as any).total_tax,
      totalPriceFinal:
        (invoice as any).total_price ??
        (invoice as any).order?.total_price?.total_after,
      discountPercentage: (invoice as any).discount_percentage,
      items:
        enrichedItems.length > 0
          ? enrichedItems
          : order?.items?.map((item, index) => ({
              id: item.id || index,
              name: item.service
                ? `خدمة #${item.service}`
                : item.subscription
                ? `اشتراك #${item.subscription}`
                : "عنصر غير محدد",
              price: item.price.toString(),
              quantity: 1,
            })) || [],
    };

    const handlePrintPaymentReceipt = async () => {
      try {
        setIsPrintingReceipt(true);
        const api = getApiInstance();
        const { data } = await api.get(
          `/store/invoices/${invoice.id}/get_matching_trn/`
        );
        // طباعة الاستجابة في الـ console حالياً حسب الطلب
        // eslint-disable-next-line no-console
        console.log("Payment receipt response:", data);
        // إن وُجدت إيصالات في الاستجابة، ابن HTML واطبع (Web أو WebView)
        const r = data?.receipts?.[0];
        if (r) {
          const html = generateNearpayReceiptHTML(r);
          const payload = { action: "printInvoice", html };
          if (window.ReactNativeWebView?.postMessage) {
            // إرسال للـ WebView (React Native) - نفس طريقة الفاتورة العادية تماماً
            window.ReactNativeWebView.postMessage(JSON.stringify(payload));
          } else {
            // Web print fallback - استخدام نفس الدالة المساعدة المستخدمة في الفاتورة
            printHtmlInBrowser(html);
          }
        } else {
          // لا توجد إيصالات في الاستجابة
          throw new Error("لا توجد بيانات إيصال متاحة");
        }
      } catch (e: any) {
        // معالجة شاملة للأخطاء مع رسائل واضحة للمستخدم
        console.error("Failed to fetch payment receipt:", e);

        let errorMessage = "فشل في جلب بيانات الإيصال، يرجى المحاولة مرة أخرى";

        // تحديد نوع الخطأ وعرض رسالة مناسبة
        if (e?.response?.status === 404) {
          errorMessage = "لم يتم العثور على بيانات الإيصال لهذه الفاتورة";
        } else if (e?.response?.status === 500) {
          errorMessage = "خطأ في الخادم، يرجى المحاولة مرة أخرى";
        } else if (e?.response?.status === 403) {
          errorMessage = "ليس لديك صلاحية لجلب بيانات الإيصال";
        } else if (e?.message && e.message !== "Failed to fetch") {
          errorMessage = e.message;
        } else if (e?.response?.data?.message) {
          errorMessage = e.response.data.message;
        }

        // عرض رسالة الخطأ للمستخدم
        alert(errorMessage);
      } finally {
        setIsPrintingReceipt(false);
      }
    };

    return (
      <div className='p-4 max-w-4xl mx-auto space-y-6 bg-white' dir='rtl'>
        <div className='flex items-center justify-between'>
          <h2 className='text-2xl font-bold text-right flex-1 mr-4'>
            تفاصيل الفاتورة #{invoice.id}
          </h2>
          <div className='flex items-center gap-3'>
            <button
              type='button'
              onClick={handleGoBack}
              className='text-red-700 hover:text-red-900 transition-colors duration-200'
              title='عودة'
            >
              <FaArrowLeft className='text-xl' />
            </button>
          </div>
        </div>

        <div className='space-y-6'>
          <InvoiceItemsScroll items={order?.items || []} />

          <div className='space-y-2'>
            <label className='text-gray-700 font-medium text-right block'>
              رقم الجوال
            </label>
            <div className='bg-white border border-gray-300 rounded-lg px-4 py-3 text-lg text-right'>
              {phoneFallback &&
              phoneFallback.trim() !== "" &&
              phoneFallback !== "-"
                ? phoneFallback
                : "غير متوفر"}
            </div>
          </div>

          <div className='space-y-2'>
            <label className='text-gray-700 font-medium text-right block'>
              كود الخصم
            </label>
            <div className='flex items-center gap-2'>
              <div className='bg-white border border-gray-300 rounded-lg px-4 py-3 text-lg flex-1 text-right'>
                {invoice.coupon_code && invoice.coupon_code !== "string"
                  ? invoice.coupon_code
                  : "لا يوجد خصم"}
              </div>
            </div>
          </div>

          <div className='space-y-2'>
            <label className='text-gray-700 font-medium text-right block'>
              مبلغ المستحق
            </label>
            <div className='bg-white border border-gray-300 rounded-lg px-4 py-3 text-lg text-right flex items-center gap-3 justify-start'>
              {discountApplied &&
              typeof backendTotalBefore === "number" &&
              typeof backendTotalAfter === "number" ? (
                <>
                  {/* النهائي على اليمين، والقبل مشطوب على اليسار */}
                  <span className='font-bold text-red-700'>
                    {backendTotalAfter.toFixed(2)} ريال
                  </span>
                  <span
                    style={{ textDecoration: "line-through", color: "#888" }}
                  >
                    {backendTotalBefore.toFixed(2)} ريال
                  </span>
                </>
              ) : (
                <span>
                  {(
                    backendSingleTotal ??
                    backendTotalAfter ??
                    backendTotalBefore ??
                    totalBefore
                  ).toFixed(2)}{" "}
                  ريال
                </span>
              )}
            </div>
          </div>

          <div className='grid grid-cols-2 gap-4'>
            <div className='space-y-2'>
              <label className='text-gray-700 font-medium text-right block'>
                كم مبلغ نقديا
              </label>
              <div className='bg-white border border-gray-300 rounded-lg px-4 py-3 text-lg text-center'>
                {invoice.cash > 0
                  ? `${invoice.cash.toFixed(2)} ريال`
                  : "0 ريال"}
              </div>
            </div>
            <div className='space-y-2'>
              <label className='text-gray-700 font-medium text-right block'>
                كم مبلغ شبكة
              </label>
              <div className='bg-white border border-gray-300 rounded-lg px-4 py-3 text-lg text-center'>
                {invoice.visa > 0
                  ? `${invoice.visa.toFixed(2)} ريال`
                  : "0 ريال"}
              </div>
            </div>
          </div>

          <div className='space-y-2'>
            <label className='text-gray-700 font-medium text-right block'>
              النقاط المكتسبة
            </label>
            <div className='bg-white border border-gray-300 rounded-lg px-4 py-3 text-lg text-center'>
              {invoice.points ? `${invoice.points} نقطة` : "0 نقطة"}
            </div>
          </div>

          <div className='bg-gray-50 rounded-lg p-4 text-right space-y-2'>
            <h3 className='text-lg font-bold text-gray-800'>ملخص الطلب</h3>
            <div className='flex justify-between items-center'>
              <span className='text-gray-700'>عدد الأصناف:</span>
              <span className='text-red-600 font-bold'>
                {totalQuantity} أصناف
              </span>
            </div>
            <div className='flex justify-between items-center'>
              <span className='text-gray-700'>السعر الكلي:</span>
              <span className='text-red-600 font-bold'>
                {totalPrice.toFixed(2)} ريال
              </span>
            </div>
            {invoice.discount_percentage > 0 && (
              <div className='flex justify-between items-center'>
                <span className='text-gray-700'>نسبة الخصم:</span>
                <span className='text-green-600 font-bold'>
                  {invoice.discount_percentage.toFixed(1)}%
                </span>
              </div>
            )}
            <div className='flex justify-between items-center'>
              <span className='text-gray-700'>رقم الجلسة:</span>
              <span className='text-blue-600 font-bold'>
                #{invoice.session}
              </span>
            </div>
          </div>

          <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
            <Button
              type='primary'
              onClick={() => setShowPrintModal(true)}
              className='w-full bg-red-700 text-white rounded-xl py-4 text-xl font-bold'
            >
              طباعة الفاتورة
            </Button>
            {/* زر طباعة إيصال الدفع - يظهر فقط عند وجود matching_trn */}
            {(invoice as any)?.matching_trn && (
              <Button
                type='secondary'
                onClick={handlePrintPaymentReceipt}
                className='w-full rounded-xl py-4 text-xl font-bold border border-red-700 text-red-700 hover:bg-red-50'
                disabled={isPrintingReceipt}
              >
                {isPrintingReceipt
                  ? "جارٍ تجهيز الإيصال..."
                  : "طباعة إيصال الدفع"}
              </Button>
            )}
          </div>
        </div>

        <div style={{ display: "none" }}>
          <div ref={printRef}>
            <InvoicePrintContent invoice={printInvoice} />
          </div>
        </div>

        <ConfirmationModal
          show={showPrintModal}
          title='تأكيد الطباعة'
          message='هل تريد طباعة الفاتورة؟'
          confirmText='نعم، اطبع'
          cancelText='إلغاء'
          onConfirm={() => {
            if (printRef.current) {
              const html = printRef.current.innerHTML;
              const payload = {
                action: "printInvoice",
                html,
              };
              if (window.ReactNativeWebView?.postMessage) {
                window.ReactNativeWebView.postMessage(JSON.stringify(payload));
              } else {
                printHtmlInBrowser(html);
              }
            }
            setShowPrintModal(false);
          }}
          onCancel={() => setShowPrintModal(false)}
        />
      </div>
    );
  };

  return (
    <QueryStateWrapper isLoading={isLoading} isError={!!error}>
      {renderContent()}
    </QueryStateWrapper>
  );
};

export default InvoiceDetails;
